/* eslint-disable max-lines-per-function */
import React from 'react';
import HeaderPage from './PrintHeaderPage';
import PrintButton from '@/print-pdf/PrintButton';
import dayjs from 'dayjs';


const { format } = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
});


const asPerc = value => `${Math.round(value * 100) }%`;

export const PrintDocument = props => {


    /** @type {{ data: { data: object[], vendors: string[] }}} */
    const {
        data,
        counts
    } = props;

    const ytdSummary = data.data.reduce((acc, property) => {
        Object.entries(property.data).forEach(([vendor, amounts]) => {
            const [dollarAmount] = amounts;
            if (!acc[vendor]) {
                acc[vendor] = 0;
            }
            acc[vendor] += dollarAmount;
        });

        return acc;
    }, {});

    const totalYtd = Object.values(ytdSummary).reduce((a, c) => a += c, 0);
    const totalTotalYtd = data.data.reduce((a, c) => a += c.total, 0);

    const monthlySummary = (
        data.data.map(property => property.components.at(-1).data)
            .reduce((acc, monthData) => {
                Object.entries(monthData).forEach(([vendor, amounts]) => {
                    const [dollarAmount] = amounts;
                    if (!acc[vendor]) {
                        acc[vendor] = 0;
                    }
                    acc[vendor] += dollarAmount;
                });

                return acc;
            }, {})
    );

    const totalMontly = Object.values(monthlySummary).reduce((a, c) => a += c, 0);
    const totalTotalMonthly = data.data.reduce((a, c) => a += c.components.at(-1).total, 0);

    const date = dayjs(data.data[0].components.at(-1).name).format('MM YYYY');

    /**
     * @type {Record<string, { columnName: string, style: {}}>}
     */
    const _headers = Object.fromEntries(
        data.vendors.map(
            columnName => [
                [columnName, {
                    columnName,
                    style: {
                        maxWidth: 40,
                        fontSize: 8.5,
                        padding: 0.5
                    }
                }],
                [`${columnName}%`, {
                    columnName: '%',
                    style: {
                        maxWidth: 22,
                        fontSize: 8.5,
                        padding: 0.5,
                        color: 'blue'
                    }
                }]
            ]
        ).flat(1)
    );

    const headers = {
        properties: {
            columnName: 'Properties',
            style: {
                maxWidth: 140,
                fontSize: 8.5,
                padding: 0.5
            }
        },
        ..._headers,
        RnMTotal: {
            columnName: 'RnM Total',
            style: {
                maxWidth: 40,
                fontSize: 8.5,
                padding: 0.5
            }
        },
        RnMPorc: {
            columnName: 'RnM %',
            style: {
                maxWidth: 22,
                fontSize: 8.5,
                padding: 0.5,
                color: 'blue'
            }
        }
    };

    /**
     * row = { [key]: value }
     */
    const rows = [];


    data.data.forEach(element => {

        const row_cell_name = {};

        row_cell_name['properties'] = {
            text: element.name.slice(0, 33)
        };

        for (const [key, value] of Object.entries(element.data)) {
            row_cell_name[key] = {
                text: format(value[0])
            };
            row_cell_name[`${key}%`] = {
                text: asPerc(value[1])
            };
            row_cell_name['RnMTotal'] = {
                text: format(element.rnmTotal[0])
            };

            row_cell_name['RnMPorc'] = {
                text: asPerc(element.rnmTotal[1])
            };
        }

        rows.push(row_cell_name);


        const month = element.components.at(-1).data;

        const row_cell_date = {};

        row_cell_date['properties'] = {
            text: element.components.at(-1).name
        };

        for (const [key, value] of Object.entries(month)) {
            row_cell_date[key] = {
                text: format(value[0])
            };
            row_cell_date[`${key}%`] = {
                text: asPerc(value[1])
            };

            row_cell_date['RnMTotal'] = {
                text: format(element.components.at(-1).rnmTotal[0])
            };

            row_cell_date['RnMPorc'] = {
                text: asPerc(element.components.at(-1).rnmTotal[1])
            };
        }


        rows.push(row_cell_date);
    });

    const row_cell_YTD = {};

    row_cell_YTD['properties'] = {
        text: 'YTD Summary'
    };

    for (const [key, value] of Object.entries(ytdSummary)) {

        row_cell_YTD[key] = {
            text: format(value)
        };
        row_cell_YTD[`${key}%`] = {
            text: asPerc(value / totalTotalYtd)
        };

    }
    row_cell_YTD['RnMTotal'] = {
        text: format(totalYtd)
    };

    row_cell_YTD['RnMPorc'] = {
        text: asPerc(totalYtd / totalTotalYtd)
    };

    rows.push(row_cell_YTD);


    const row_cell_Month = {};

    row_cell_Month['properties'] = {
        text: 'Selected Month Summary'
    };

    for (const [key, value] of Object.entries(monthlySummary)) {

        row_cell_Month[key] = {
            text: format(value)
        };
        row_cell_Month[`${key}%`] = {
            text: asPerc(value / totalTotalMonthly)
        };

    }
    row_cell_Month['RnMTotal'] = {
        text: format(totalMontly)
    };

    row_cell_Month['RnMPorc'] = {
        text: asPerc(totalMontly / totalTotalMonthly)
    };

    rows.push(row_cell_Month);


    return (
        <PrintButton
            orientation = { 'portrait' }
            headers={headers}
            data={rows}
            rowsPerPage={counts}
            HeaderPage={() => (<HeaderPage
                date={ date }
            />)}
            // evenRowColor={'blue'}
            // oddRowColor={'red'}
            zebra={false}
        />
    );

};
